import * as React from "react";
import { currentlyFocusedElement } from "../../utils";

import { InputContainer } from "./input-container";
import { SingleLineTextInputProps } from "./single-line-text-input";

interface OneTimePasswordInputProps
  extends Omit<SingleLineTextInputProps, "autoComplete"> {
  guid: string;
  passwordLength: number;
}

const NULL_CURSOR = -1;

export const OneTimePasswordInput: React.FunctionComponent<
  OneTimePasswordInputProps
> = (props) => {
  const thisEventNameBase = `bbnt-common-form-ui-one-time-password-input-${props.guid}`;

  const [previousValue, setPreviousValue] = React.useState<
    string | undefined
  >();
  const [currentCursor, setCurrentCursor] = React.useState(NULL_CURSOR);
  const [waitingForCursorToReconcile, setWaitingForCursorToReconcile] =
    React.useState(false);

  const [currentValue, isPlaceholder] = React.useMemo(() => {
    let isPlaceholder = true;
    let currentValue = props.placeholder;

    const currentFormValue = props.form[props.name];
    if (!!currentFormValue && currentFormValue.length > 0) {
      currentValue = currentFormValue;
      isPlaceholder = false;
    }

    return [currentValue || "", isPlaceholder];
  }, [props.placeholder, props.form, props.name]);

  React.useEffect(() => {
    setCurrentCursor(NULL_CURSOR);
  }, [props.passwordLength]);

  React.useEffect(() => {
    if (currentValue && !isPlaceholder) {
      setPreviousValue(currentValue);
    } else {
      setPreviousValue(undefined);
    }
  }, [currentValue, isPlaceholder]);

  const moveCursorToTheNextBox = React.useCallback(() => {
    if (currentCursor < props.passwordLength) {
      setWaitingForCursorToReconcile(true);
      setCurrentCursor(currentCursor + 1);
    } else {
      setCurrentCursor(NULL_CURSOR);

      const focusedElement = currentlyFocusedElement();
      if (focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }
    }
  }, [props.passwordLength, currentCursor]);
  const moveCursorToTheNextUnfilledBox = React.useCallback(() => {
    const INVALID_CELL = -1;

    let nextCursor: number = INVALID_CELL;
    new Array(props.passwordLength)
      .fill(true)
      .forEach((_, thisPasswordInputBoxCursor) => {
        const weDontHaveANextCursorYet = nextCursor === INVALID_CELL;
        if (weDontHaveANextCursorYet) {
          const isTheCurrentCursor_behind_thisPasswordInputBoxCursor_meaning_isThisAnInputBoxForwardFromMyPositionInTheInputBoxes =
            currentCursor < thisPasswordInputBoxCursor;
          if (
            isTheCurrentCursor_behind_thisPasswordInputBoxCursor_meaning_isThisAnInputBoxForwardFromMyPositionInTheInputBoxes
          ) {
            const thisCellValue_whereThisCellIsThePotentialNextCell =
              currentValue[thisPasswordInputBoxCursor];

            const isTheValueFilledInAlready_inThisCell_whereThisCellIsThePotentialNextCell =
              thisCellValue_whereThisCellIsThePotentialNextCell &&
              thisCellValue_whereThisCellIsThePotentialNextCell.length > 0;

            const canThisBeTheNextCell =
              !isTheValueFilledInAlready_inThisCell_whereThisCellIsThePotentialNextCell;

            if (canThisBeTheNextCell) {
              nextCursor = thisPasswordInputBoxCursor;
            }
          }
        }
      });

    const isThereANextCursorToGoto = nextCursor !== INVALID_CELL;
    if (isThereANextCursorToGoto) {
      setWaitingForCursorToReconcile(true);
      setCurrentCursor(nextCursor);
    } else {
      setCurrentCursor(NULL_CURSOR);
      const focusedElement = currentlyFocusedElement();
      if (focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }
    }
  }, [props.passwordLength, currentCursor, currentValue]);

  const moveCursorToThePreviousBox = React.useCallback(() => {
    if (currentCursor > 0) {
      setWaitingForCursorToReconcile(true);
      setCurrentCursor(currentCursor - 1);
    }
  }, [currentCursor]);

  const refreshCursor = React.useCallback(() => {
    const whereIsTheCursorInThePreviousContent = currentCursor;

    const previousValue_atCurrentCursor =
      previousValue &&
      (previousValue?.length || 0) > whereIsTheCursorInThePreviousContent
        ? previousValue[whereIsTheCursorInThePreviousContent]
        : "";
    const currentValue_atCurrentCursor =
      currentValue &&
      (currentValue?.length || 0) > whereIsTheCursorInThePreviousContent
        ? currentValue[whereIsTheCursorInThePreviousContent]
        : "";

    const isTheNextContentDifferentAtTheCursorsCurrentPosition =
      currentValue_atCurrentCursor !== previousValue_atCurrentCursor;
    const isTheNextContentValueAtTheCursorsPositionFilledInWithAValue =
      currentValue_atCurrentCursor.length > 0;

    let cursorPositionHasJustBeenFilled =
      isTheNextContentDifferentAtTheCursorsCurrentPosition &&
      isTheNextContentValueAtTheCursorsPositionFilledInWithAValue;
    if (cursorPositionHasJustBeenFilled) {
      moveCursorToTheNextUnfilledBox();
    }
  }, [
    currentCursor,
    previousValue,
    currentValue,
    moveCursorToTheNextUnfilledBox,
  ]);

  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-current-value-changed`,
      refreshCursor
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-current-value-changed`,
        refreshCursor
      );
    };
  }, [currentCursor, previousValue, currentValue, moveCursorToTheNextBox]);
  React.useEffect(() => {
    window.dispatchEvent(
      new Event(`${thisEventNameBase}-current-value-changed`)
    );
  }, [currentValue]);

  const handleChange = React.useCallback(
    (changedValue, atIndex) => {
      let newValue = currentValue;
      if (atIndex < currentValue.length) {
        newValue = `${currentValue.substring(
          0,
          atIndex
        )}${changedValue}${currentValue.substring(atIndex + 1)}`;
      } else {
        newValue = `${currentValue}${changedValue}`;
      }

      props.onChange(props.name, newValue);
    },
    [props.name, props.onChange, currentValue, moveCursorToTheNextBox]
  );

  const handleFocus = React.useCallback(
    (cellIndex: number) => {
      if (!waitingForCursorToReconcile) {
        setCurrentCursor(cellIndex);
        props.onFocus?.(props.name);
      }
      setWaitingForCursorToReconcile(false);
    },
    [waitingForCursorToReconcile, props.onFocus, props.name]
  );

  const handleBlur = React.useCallback(() => {
    if (!waitingForCursorToReconcile) {
      props.onBlur?.(props.name);
      setCurrentCursor(NULL_CURSOR);
    }
  }, [waitingForCursorToReconcile, props.onBlur, props.name]);

  const handlePaste = React.useCallback(
    (
      event: Parameters<React.ClipboardEventHandler<HTMLInputElement>>[0],
      startingCellIndex
    ) => {
      event.preventDefault();

      const pastedData = event.clipboardData.getData("text/plain");

      let newValue = `${currentValue.substring(
        0,
        startingCellIndex
      )}${pastedData}`;

      props.onChange(props.name, newValue);
    },
    [currentValue, props.onChange, props.name]
  );

  const handleBackspace = React.useCallback(
    (inputCellIndex: number) => {
      const thisCellValue = currentValue[inputCellIndex] || "";

      if (thisCellValue.length > 0) {
        handleChange("", inputCellIndex);
      } else {
        const cellIndexOfThePreviousBox = inputCellIndex - 1;
        if (cellIndexOfThePreviousBox >= 0) {
          handleChange("", cellIndexOfThePreviousBox);
          moveCursorToThePreviousBox();
        }
      }
    },
    [currentValue, handleChange, moveCursorToThePreviousBox]
  );

  const inputBoxes = React.useMemo(() => {
    return new Array(props.passwordLength).fill(true).map((_, index) => {
      const thisCellValue =
        currentValue.length > index ? currentValue[index] : "";

      const handleThisCellChange: OneTimePasswordInput_CellProps["handleChange"] =
        (thisCellNewValue) => {
          handleChange(thisCellNewValue, index);
        };

      const handleThisCellOnPaste: React.ClipboardEventHandler<
        HTMLInputElement
      > = (event) => {
        handlePaste(event, index);
      };

      const handleThisCellBackspace = () => {
        handleBackspace(index);
      };

      return (
        <OneTimePasswordInput_Cell
          value={thisCellValue}
          type={props.type}
          isPlaceholder={isPlaceholder}
          onFocus={() => handleFocus(index)}
          handleChange={handleThisCellChange}
          onBlur={handleBlur}
          focus={currentCursor === index}
          moveCursorToThePreviousBox={moveCursorToThePreviousBox}
          moveCursorToTheNextBox={moveCursorToTheNextBox}
          handleOnPaste={handleThisCellOnPaste}
          handleBackspace={handleThisCellBackspace}
        />
      );
    });
  }, [
    props.passwordLength,
    currentValue,
    isPlaceholder,
    props.type,
    handleFocus,
    handleChange,
    handleBlur,
    handlePaste,
    handleBackspace,
    currentCursor,
  ]);

  if (props.passwordLength < 1) {
    return null;
  }

  return (
    <InputContainer
      {...props}
      className={`one-time-password-input ${props?.className || ""}`}
    >
      <div className="input-immediate-container">{inputBoxes}</div>
    </InputContainer>
  );
};

interface OneTimePasswordInput_CellProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  isPlaceholder?: boolean;
  type: SingleLineTextInputProps["type"];
  focus?: boolean;
  handleChange: (newValue: string) => void;
  moveCursorToThePreviousBox: () => void;
  moveCursorToTheNextBox: () => void;
  handleOnPaste: React.ClipboardEventHandler<HTMLInputElement>;
  handleBackspace: () => void;
}

const OneTimePasswordInput_Cell: React.FunctionComponent<
  OneTimePasswordInput_CellProps
> = (props) => {
  const [currentFocusState, setCurrentFocusState] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (props.focus !== currentFocusState) {
      if (props.focus) {
        inputRef?.current?.focus();
        setCurrentFocusState(true);
      } else {
        setCurrentFocusState(false);
      }
    }
  }, [props.focus, currentFocusState]);

  const handleKeyDown = React.useCallback(
    (event) => {
      const BACKSPACE = 8;
      const LEFT_ARROW = 37;
      const RIGHT_ARROW = 39;
      const DELETE = 46;

      if (event.keyCode === BACKSPACE || event.key === "Backspace") {
        event.preventDefault();
        props.handleBackspace();
      } else if (event.keyCode === DELETE || event.key === "Delete") {
        event.preventDefault();
        props.handleChange("");
      } else if (event.keyCode === LEFT_ARROW || event.key === "ArrowLeft") {
        event.preventDefault();
        props.moveCursorToThePreviousBox();
      } else if (event.keyCode === RIGHT_ARROW || event.key === "ArrowRight") {
        event.preventDefault();
        props.moveCursorToTheNextBox();
      }
    },
    [
      props.handleChange,
      props.moveCursorToThePreviousBox,
      props.moveCursorToTheNextBox,
      props.handleBackspace,
    ]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        const newValue = event.currentTarget.value;
        props.handleChange(newValue);
      },
      [props.handleChange]
    );

  return (
    <input
      {...props}
      type={props.type || "text"}
      maxLength={1}
      autoComplete="bbnt-one-time-password-input-cell"
      inputMode="numeric"
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onPaste={props.handleOnPaste}
      ref={inputRef}
    ></input>
  );
};
